import type { PortfolioConferenceSummary } from "./portfolio-overview-api";
import type { AppLocale } from "./localization";
import { localizedConferenceName } from "./conference-names.ts";

export type ConferenceFilter = "all" | "active" | "upcoming" | "ended";
export type ConferenceSort = "event-date" | "name" | "last-synchronized";

export interface ConferenceDirectoryQuery {
  readonly filter: ConferenceFilter;
  readonly search: string;
  readonly sort: ConferenceSort;
  readonly locale?: AppLocale;
}

const SEARCH_MARKS_AND_TATWEEL = /[\p{M}\u0640]/gu;

export function normalizeConferenceSearchText(
  value: string,
  locale: AppLocale,
): string {
  return value
    .normalize("NFKD")
    .replace(SEARCH_MARKS_AND_TATWEEL, "")
    .toLocaleLowerCase(locale)
    .trim();
}

export function queryConferences(
  conferences: readonly PortfolioConferenceSummary[],
  query: ConferenceDirectoryQuery,
): readonly PortfolioConferenceSummary[] {
  const locale = query.locale ?? "en";
  const search = normalizeConferenceSearchText(query.search, locale);
  const collator = new Intl.Collator(locale, {
    usage: "sort",
    sensitivity: "base",
    numeric: true,
  });
  return conferences
    .map((conference) => ({
      ...conference,
      name: localizedConferenceName(conference, locale),
    }))
    .filter(
      (conference) =>
        (query.filter === "all" || conference.lifecycle === query.filter) &&
        (search.length === 0 ||
          normalizeConferenceSearchText(conference.name, locale).includes(
            search,
          )),
    )
    .sort((left, right) => {
      if (query.sort === "name") return collator.compare(left.name, right.name);
      if (query.sort === "last-synchronized") {
        const leftTime = left.lastSuccessfulSyncAt
          ? Date.parse(left.lastSuccessfulSyncAt)
          : Number.NEGATIVE_INFINITY;
        const rightTime = right.lastSuccessfulSyncAt
          ? Date.parse(right.lastSuccessfulSyncAt)
          : Number.NEGATIVE_INFINITY;
        return rightTime - leftTime || collator.compare(left.name, right.name);
      }
      return (
        Date.parse(left.startsAt) - Date.parse(right.startsAt) ||
        collator.compare(left.name, right.name)
      );
    });
}

export type AppRoute =
  | { readonly page: "overview" }
  | { readonly page: "conferences" }
  | { readonly page: "revenue" }
  | { readonly page: "expenses" }
  | { readonly page: "users" }
  | { readonly page: "connect" }
  | {
      readonly page: "conference";
      readonly conferenceId: string;
      readonly detailView: "overview" | "analytics" | "finance" | "information";
    }
  | { readonly page: "future" };

export function parseAppRoute(hash: string): AppRoute {
  const normalized = hash.replace(/^#/, "") || "/";
  if (normalized === "/") return Object.freeze({ page: "overview" });
  if (normalized === "/conferences") {
    return Object.freeze({ page: "conferences" });
  }
  if (normalized === "/revenue") {
    return Object.freeze({ page: "revenue" });
  }
  if (normalized === "/expenses") {
    return Object.freeze({ page: "expenses" });
  }
  if (normalized === "/users") {
    return Object.freeze({ page: "users" });
  }
  if (normalized === "/connect") {
    return Object.freeze({ page: "connect" });
  }
  const conference = normalized.match(
    /^\/conferences\/([^/]+)(?:\/(analytics|finance|information))?$/,
  );
  if (conference?.[1]) {
    let conferenceId: string;
    try {
      conferenceId = decodeURIComponent(conference[1]);
    } catch {
      return Object.freeze({ page: "future" });
    }
    return Object.freeze({
      page: "conference",
      conferenceId,
      detailView:
        conference[2] === "analytics" ||
        conference[2] === "finance" ||
        conference[2] === "information"
          ? conference[2]
          : "overview",
    });
  }
  return Object.freeze({ page: "future" });
}
